import type {
  ProjectService,
  ProjectSubservice,
} from "../../ProjectServicesV2/types";
import type { LineType, RevisedValues, ScopeLine, ScopeService } from "./types";
import { formatHours } from "./format";

export const lineKey = (type: LineType, id: string) => `${type}:${id}`;

const num = (value: unknown, fallback: number) => {
  const n = Number(value);
  return value === undefined || value === null || value === "" || isNaN(n)
    ? fallback
    : n;
};

// useListProjectServices encodes the resource as "project-resources-<id>".
const resourceIdOf = (value?: string) =>
  value ? value.split("-").pop() ?? null : null;

const toLine = (
  type: LineType,
  item: ProjectService | ProjectSubservice
): ScopeLine => ({
  key: lineKey(type, item.id),
  type,
  id: item.id,
  name: item.name,
  quantity: num(item.quantity, 1),
  effort: num(item.hours, 0),
  resourceId: resourceIdOf(item.resource?.value),
  resourceLabel: item.resource?.label ?? "",
  customHours: item.variableRates === true,
});

export const toScopeServices = (services: ProjectService[]): ScopeService[] =>
  services.map((service) => ({
    line: toLine("project-services", service),
    subservices: (service.subservices ?? []).map((sub) =>
      toLine("project-subservices", sub)
    ),
  }));

export const flattenLines = (services: ScopeService[]): ScopeLine[] =>
  services.flatMap((s) => [s.line, ...s.subservices]);

// A custom-hours line's effort is the catalog-calculated TOTAL for the line
// (the server stores it in override_hours), so it isn't multiplied by quantity.
export const originalHours = (
  line: Pick<ScopeLine, "quantity" | "effort" | "customHours">
) => (line.customHours ? line.effort : line.quantity * line.effort);

export const originalNote = (
  line: Pick<ScopeLine, "quantity" | "effort" | "customHours">
) =>
  line.customHours
    ? `Original: ${formatHours(line.quantity)} — ${formatHours(originalHours(line))} hrs (catalog)`
    : `Original: ${formatHours(line.quantity)} × ${formatHours(line.effort)} hrs`;

// The grid's values are the change request line's own (what lands on the
// change request project), not revised totals. Matches ChangeOrderDelta:
// quantity defaults to the original, effort to 0; a custom-hours line is
// priced by the catalog from its quantity, which defaults to 0.
export const defaultAdded = (line: ScopeLine) =>
  line.customHours
    ? { quantity: 0, effort: 0 }
    : { quantity: line.quantity, effort: 0 };

export const addedValues = (line: ScopeLine, revised?: RevisedValues) => {
  const defaults = defaultAdded(line);
  return {
    quantity: revised?.quantity ?? defaults.quantity,
    effort: revised?.effort ?? defaults.effort,
  };
};

// null for a custom-hours line that adds hours: the preview prices it.
export const addedHours = (
  line: ScopeLine,
  revised?: RevisedValues
): number | null => {
  const { quantity, effort } = addedValues(line, revised);
  if (line.customHours) return quantity > 0 ? null : 0;
  return quantity * effort;
};

export const addsHours = (line: ScopeLine, revised?: RevisedValues) => {
  const { quantity, effort } = addedValues(line, revised);
  return line.customHours ? quantity > 0 : quantity * effort > 0;
};
